import { useLocation, useNavigate } from "react-router-dom";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import React, { ChangeEvent, useEffect, useState } from "react";
import { IMAGE_URL } from "../../path";
import {
    getHomePageContent,
    updateHomePageSection,
    getCoreCapabilities,
} from "../../actions/HomePageImages";

type HomeSection = {
    id: number;
    title: string;
    image: string;
    left_text: string;
    right_text: string;
    position: number;
};

type CoreCapability = {
    id: number;
    image: string;
    top_text: string;
    bottom_text: string;
    right_text: string;
    position: number;
};

type SectionState = HomeSection & {
    newImage: File | null;
    previewUrl: string;
    isSaving: boolean;
    message: string;
    error: string;
};

const sectionNames: Record<number, string> = {
    1: "Hero",
    2: "Intro Section",
    3: "Our Capabilities",
    4: "Core Capabilities",
    5: "View Portfolio",
    6: "We Are Invested in Your Future",
    7: "Ready to Grow Your Brand",
    8: "View Our Portfolio",
};

function ViewHome() {
    const location = useLocation();
    const navigate = useNavigate();
    const user = location.state?.user;

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [sections, setSections] = useState<SectionState[]>([]);
    const [capabilities, setCapabilities] = useState<CoreCapability[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState("");

    useEffect(() => {
        if (!location.state?.user) {
            navigate("/login");
        }
    }, [location.state, navigate]);

    useEffect(() => {
        fetchHomePage();
        fetchCoreCapabilities();
    }, []);

    const fetchHomePage = async () => {
        setIsLoading(true);
        setPageError("");

        try {
            const response = await getHomePageContent();

            const data: HomeSection[] = response.data;

            const formattedSections: SectionState[] = data
                .sort((a, b) => a.position - b.position)
                .map(section => ({
                    id: section.id,
                    title: section.title ?? "",
                    image: section.image ?? "",
                    left_text: section.left_text ?? "",
                    right_text: section.right_text ?? "",
                    position: section.position,
                    newImage: null,
                    previewUrl: "",
                    isSaving: false,
                    message: "",
                    error: "",
                }));

            setSections(formattedSections);
        } catch (error) {
            console.error("Failed to fetch homepage content:", error);
            setPageError("Failed to load homepage content.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCoreCapabilities = async () => {
        try {
            const response = await getCoreCapabilities();

            setCapabilities(response.data);
        } catch (error) {
            console.error(
                "Failed to fetch core capabilities:",
                error
            );
        }
    };

    const handleFieldChange = (
        id: number,
        field: "title" | "left_text" | "right_text",
        value: string
    ) => {
        setSections(current =>
            current.map(section =>
                section.id === id
                    ? {
                        ...section,
                        [field]: value,
                        message: "",
                        error: "",
                    }
                    : section
            )
        );
    };

    const handleImageChange = (
        id: number,
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        const validTypes = [
            "image/jpeg",
            "image/png",
            "image/webp",
        ];

        if (!validTypes.includes(file.type)) {
            setSections(current =>
                current.map(section =>
                    section.id === id
                        ? {
                            ...section,
                            error:
                                "Only JPG, PNG or WebP images are allowed.",
                            message: "",
                        }
                        : section
                )
            );

            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setSections(current =>
                current.map(section =>
                    section.id === id
                        ? {
                            ...section,
                            error: "Image must be 2MB or smaller.",
                            message: "",
                        }
                        : section
                )
            );

            return;
        }

        const previewUrl = URL.createObjectURL(file);

        setSections(current =>
            current.map(section => {
                if (section.id !== id) {
                    return section;
                }

                if (section.previewUrl) {
                    URL.revokeObjectURL(section.previewUrl);
                }

                return {
                    ...section,
                    newImage: file,
                    previewUrl,
                    error: "",
                    message: "",
                };
            })
        );
    };

    const removeImage = (id: number) => {
        setSections(current =>
            current.map(section => {
                if (section.id !== id) {
                    return section;
                }

                if (section.previewUrl) {
                    URL.revokeObjectURL(section.previewUrl);
                }

                return {
                    ...section,
                    newImage: null,
                    previewUrl: "",
                    image: "",
                    message: "",
                    error: "",
                };
            })
        );
    };

    const saveSection = async (section: SectionState) => {
        setSections(current =>
            current.map(item =>
                item.id === section.id
                    ? {
                        ...item,
                        isSaving: true,
                        message: "",
                        error: "",
                    }
                    : item
            )
        );

        try {
            const response = await updateHomePageSection({
                id: section.id,
                title: section.title,
                left_text: section.left_text,
                right_text: section.right_text,
                image: section.newImage || undefined,
            });

            const updatedSection: HomeSection = response.data.data;

            setSections(current =>
                current.map(item =>
                    item.id === section.id
                        ? {
                            ...item,
                            ...updatedSection,
                            title: updatedSection.title ?? "",
                            image: updatedSection.image ?? "",
                            left_text:
                                updatedSection.left_text ?? "",
                            right_text:
                                updatedSection.right_text ?? "",
                            newImage: null,
                            previewUrl: "",
                            isSaving: false,
                            message:
                                "Changes saved successfully.",
                            error: "",
                        }
                        : item
                )
            );
        } catch (error) {
            console.error(
                `Failed to update homepage section ${section.id}:`,
                error
            );

            setSections(current =>
                current.map(item =>
                    item.id === section.id
                        ? {
                            ...item,
                            isSaving: false,
                            message: "",
                            error:
                                "Failed to save changes. Please try again.",
                        }
                        : item
                )
            );
        }
    };

    const addCapability = () => {
        navigate("/dashboard/home/add-capability", {
            state: { user },
        });
    };

    const editCapability = (capability: CoreCapability) => {
        navigate("/dashboard/home/edit-capability", {
            state: {
                user,
                capability,
            },
        });
    };

    if (isLoading) {
        return (
            <div>
                <AdminHeader
                    isCollapsed={isCollapsed}
                    setIsCollapsed={setIsCollapsed}
                    user={user}
                />

                <AdminNav
                    collapsed={isCollapsed}
                    setCollapsed={setIsCollapsed}
                    user={user}
                />

                <div
                    className={`main-admin-page ${
                        isCollapsed ? "collapsed" : "expanded"
                    }`}
                >
                    <div className="main-admin-page-inner">
                        <h1>Home Page</h1>
                        <p>Loading homepage content...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <AdminHeader
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                user={user}
            />

            <AdminNav
                collapsed={isCollapsed}
                setCollapsed={setIsCollapsed}
                user={user}
            />

            <div
                className={`main-admin-page ${
                    isCollapsed ? "collapsed" : "expanded"
                }`}
            >
                <div className="main-admin-page-inner">
                    <h1>Home Page</h1>

                    {pageError && (
                        <div
                            className="alert error"
                            style={{ marginBottom: "20px" }}
                        >
                            {pageError}
                        </div>
                    )}

                    {sections.map(section => (
                        <div
                            key={section.id}
                            className="home-admin-section"
                            style={{
                                marginBottom: "40px",
                                padding: "25px",
                                border: "1px solid #ddd",
                            }}
                        >
                            <h2>
                                {sectionNames[section.position] ||
                                    `Section ${section.position}`}
                            </h2>

                            {section.position !== 1 && (
                                <div className="content-form-field">
                                    <label>Title</label>

                                    <input
                                        type="text"
                                        className="input-email"
                                        value={section.title}
                                        onChange={e =>
                                            handleFieldChange(
                                                section.id,
                                                "title",
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            )}

                            {[3, 4, 5].includes(section.position) && (
                                <div className="content-form-field">
                                    <label>Text</label>

                                    <textarea
                                        className="input-email"
                                        rows={7}
                                        value={section.left_text}
                                        onChange={e =>
                                            handleFieldChange(
                                                section.id,
                                                "left_text",
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            )}

                            {section.position === 5 && (
                                <div className="content-form-field">
                                    <label>Right Text</label>

                                    <textarea
                                        className="input-email"
                                        rows={7}
                                        value={section.right_text}
                                        onChange={e =>
                                            handleFieldChange(
                                                section.id,
                                                "right_text",
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            )}

                            {[1, 3, 6, 7, 8].includes(section.position) && (
                                <div className="content-form-field">
                                    <label>Image</label>

                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={e =>
                                            handleImageChange(section.id, e)
                                        }
                                        style={{ marginTop: "10px" }}
                                    />

                                    {section.error && (
                                        <div
                                            style={{
                                                color: "red",
                                                marginTop: "10px",
                                            }}
                                        >
                                            {section.error}
                                        </div>
                                    )}

                                    {(section.previewUrl || section.image) && (
                                        <div style={{ marginTop: "15px" }}>
                                            <strong>Preview</strong>

                                            <br />

                                            <img
                                                src={
                                                    section.previewUrl ||
                                                    IMAGE_URL + section.image
                                                }
                                                alt={
                                                    section.title ||
                                                    sectionNames[section.position]
                                                }
                                                style={{
                                                    maxWidth: "500px",
                                                    maxHeight: "300px",
                                                    objectFit: "contain",
                                                    marginTop: "10px",
                                                }}
                                            />

                                            {section.previewUrl && (
                                                <div style={{ marginTop: "10px" }}>
                                                    <button
                                                        type="button"
                                                        className="button small danger"
                                                        onClick={() =>
                                                            removeImage(section.id)
                                                        }
                                                    >
                                                        Remove Image
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="content-form-button">
                                <button
                                    type="button"
                                    disabled={section.isSaving}
                                    onClick={() =>
                                        saveSection(section)
                                    }
                                >
                                    {section.isSaving
                                        ? "Saving..."
                                        : "Save Changes"}
                                </button>
                            </div>

                            {section.message && (
                                <div
                                    className="alert success"
                                    style={{
                                        marginTop: "15px",
                                    }}
                                >
                                    {section.message}
                                </div>
                            )}
                        </div>
                    ))}

                    <div
                        className="home-admin-section"
                        style={{
                            marginBottom: "40px",
                            padding: "25px",
                            border: "1px solid #ddd",
                        }}
                    >
                        <h2>Core Capabilities</h2>

                        <p>
                            Manage the capability cards displayed in the Core
                            Capabilities section of the homepage.
                        </p>

                        <div
                            className="content-form-button right-align"
                            style={{
                                marginBottom: "20px",
                            }}
                        >
                            <button
                                type="button"
                                className="button button-right"
                                onClick={addCapability}
                            >
                                Add Capability
                            </button>
                        </div>

                        {capabilities.length > 0 ? (
                            <div className="table-container">
                                <table className="home-images-table">
                                    <thead>
                                    <tr>
                                        <th>Image</th>
                                        <th>Top Text</th>
                                        <th>Bottom Text</th>
                                        <th>Right Text</th>
                                        <th>Position</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {capabilities.map(capability => (
                                        <tr key={capability.id}>
                                            <td>
                                                {capability.image && (
                                                    <img
                                                        src={
                                                            IMAGE_URL +
                                                            capability.image
                                                        }
                                                        alt={
                                                            capability.bottom_text
                                                        }
                                                        style={{
                                                            width: "150px",
                                                        }}
                                                    />
                                                )}
                                            </td>

                                            <td>
                                                {capability.top_text}
                                            </td>

                                            <td>
                                                {capability.bottom_text}
                                            </td>

                                            <td>
                                                {capability.right_text}
                                            </td>

                                            <td>
                                                {capability.position}
                                            </td>

                                            <td>
                                                <button
                                                    type="button"
                                                    className="button small"
                                                    onClick={() =>
                                                        editCapability(
                                                            capability
                                                        )
                                                    }
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p>No capabilities found.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ViewHome;
